import type { LocalAiRuntime, SpeechModel, SpeechModelKey, VoiceModel } from '../types/LocalAiTypes';

/** The manifest key each speech model is published under. */
export const speechModelKeys: Readonly<Record<SpeechModel, SpeechModelKey>> = {
  turbo: 'stt.turbo',
  small: 'stt.small',
};

/** One thing to try: a checkpoint, and the backend to run it on. */
export interface SpeechAttempt {
  readonly model: SpeechModelKey;
  readonly runtime: LocalAiRuntime;
}

export function isSpeechModelKey(key: VoiceModel['key']): key is SpeechModelKey {
  return key === 'stt.turbo' || key === 'stt.small';
}

/**
 * Which checkpoint this browser can actually run.
 *
 * A GPU question, and only that. Turbo is 537-724 MB of four-bit weights and there is no
 * arrangement of them the CPU backend transcribes at a speed anybody would wait through, so a
 * machine without WebGPU gets Small whatever the setting says, and the settings screen says why
 * rather than offering a choice that cannot work.
 *
 * Which *build* of turbo then runs - the half-precision one or the fp32 one - is a separate
 * question with a separate answer, and it belongs to `resolveModel` rather than here. It used
 * to be tangled into this decision, which is how a GPU lacking `shader-f16` ended up being
 * handed Whisper Small: a limitation of one build of a checkpoint, mistaken for a limitation of
 * the checkpoint.
 */
export function resolveSpeechModel(preferred: SpeechModel, runtime: LocalAiRuntime): SpeechModel {
  return runtime === 'webgpu' ? preferred : 'small';
}

/**
 * What to try, in order, when the microphone opens.
 *
 * There is a second entry only where there is a real second chance. Turbo on WebGPU can fail
 * for reasons that have nothing to do with this code — a driver that refuses the adapter, a
 * machine that cannot spare the memory — and Small on WASM is the thing that still works when
 * it does. But only if Small is actually on the device: an attempt at a checkpoint nobody has
 * downloaded turns one clear failure into two confusing ones, so it is left out unless its
 * files are already cached.
 */
export function speechAttempts(
  preferred: SpeechModel,
  runtime: LocalAiRuntime,
  installed: Readonly<Record<SpeechModel, boolean>>,
): readonly SpeechAttempt[] {
  const resolved = resolveSpeechModel(preferred, runtime);
  if (resolved === 'small') {
    /*
     * Small is eight-bit, so it runs on either backend, and the second entry costs nothing:
     * the files are the same ones already on the device. It is there for the driver that
     * accepts a WebGPU session and then cannot execute it — the same class of failure that
     * makes the turbo path need a fallback.
     */
    const attempts: SpeechAttempt[] = [{ model: speechModelKeys.small, runtime }];
    if (runtime === 'webgpu') attempts.push({ model: speechModelKeys.small, runtime: 'wasm' });
    return attempts;
  }
  const attempts: SpeechAttempt[] = [{ model: speechModelKeys.turbo, runtime: 'webgpu' }];
  if (installed.small) attempts.push({ model: speechModelKeys.small, runtime: 'wasm' });
  return attempts;
}
